import BaseImporter from "./base-importer";
import SportService from "../sport-service";
import PlayerService from "../player-service";

const FIELDS = {
    NUMBER: "number",
    FIRST_NAME: "firstName",
    LAST_NAME: "lastName",
    POSITION: "position",
    PLAYER_MATCH: "playerMatch"
};

const requiredHeaders = [
    FIELDS.NUMBER,
    FIELDS.FIRST_NAME,
    FIELDS.LAST_NAME,
    FIELDS.POSITION
];

class FootballImporter extends BaseImporter {
    findPlayerMatch(p, players) {
        return players.find((ep) => ep.uniform === p[FIELDS.NUMBER]);
    }

    findPositionMatch(p, positions) {
        const playerPositions = p.position ? p.position.split("/") : [];

        return (
            positions &&
            positions.filter((ep) =>
                playerPositions.find(
                    (pp) =>
                        pp === ep.code ||
                        pp.trim().localeCompare(ep.name, undefined, {
                            sensitivity: "base"
                        }) === 0
                )
            )
        );
    }

    getRequiredHeaders() {
        return requiredHeaders;
    }

    getOptionalHeaders() {
        return [];
    }

    isValidPlayerMatch(records, r) {
        const { playerMatch } = r;
        if (playerMatch && r.number !== playerMatch.uniform) {
            const selectedUniforms = records
                .map((r) => r.playerMatch?.uniform)
                .filter((u) => u);

            const hasDuplicates = selectedUniforms.some(
                (r, index) => selectedUniforms.indexOf(r) !== index
            );

            return !hasDuplicates;
        }

        return true;
    }

    validateRecord(records, r) {
        r.errors = {};

        if (this.isEmpty(r, FIELDS.FIRST_NAME)) {
            Object.assign(r.errors, {
                [FIELDS.FIRST_NAME]: ["First name is required"]
            });
        }
        if (this.isEmpty(r, FIELDS.LAST_NAME)) {
            Object.assign(r.errors, {
                [FIELDS.LAST_NAME]: ["Last name is required"]
            });
        }
        if (this.isFieldDuplicated(records, r, FIELDS.NUMBER)) {
            Object.assign(r.errors, {
                [FIELDS.NUMBER]: ["Number duplicated"]
            });
        }
        if (this.isEmpty(r, FIELDS.NUMBER)) {
            Object.assign(r.errors, {
                [FIELDS.NUMBER]: ["Number is required"]
            });
        }
        if (
            r[FIELDS.NUMBER] &&
            !this.matches(r, FIELDS.NUMBER, /^[A-Za-z0-9]{1,3}$/)
        ) {
            Object.assign(r.errors, {
                [FIELDS.NUMBER]: [
                    "Number must contain up to 3 alpha characters"
                ]
            });
        }
        if (!this.isValidPlayerMatch(records, r)) {
            Object.assign(r.errors, {
                [FIELDS.PLAYER_MATCH]: ["Player already selected"]
            });
        }
    }

    validateRecords(records) {
        records.forEach((r) => this.validateRecord(records, r));
    }

    mapCsvPlayer(csvPlayer, players, positions, index) {
        return {
            importIndex: index,
            number: this.readCsvValue(csvPlayer, FIELDS.NUMBER),
            firstName: this.readCsvValue(csvPlayer, FIELDS.FIRST_NAME),
            lastName: this.readCsvValue(csvPlayer, FIELDS.LAST_NAME),
            playerMatch: this.findPlayerMatch(csvPlayer, players),
            positionMatch: this.findPositionMatch(csvPlayer, positions),
            errors: {}
        };
    }

    async mapCsvPlayers(teamId, sportId, data) {
        const positions = await SportService.getPlayerPositions(sportId);
        const players = await PlayerService.getPlayers(teamId);

        return data.map((d, index) =>
            this.mapCsvPlayer(d, players, positions, index)
        );
    }

    async savePlayers(teamId, players) {
        const toUpdate = players.map((p) => {
            const player = p.playerMatch || {};

            player.teamId = teamId;
            player.uniform = p.number;
            player.firstName = p.firstName;
            player.lastName = p.lastName;
            player.position = p.positionMatch;

            return player;
        });

        return PlayerService.savePlayers(teamId, toUpdate);
    }
}

export default new FootballImporter();
